// ====================================================================
// QUICK START PANEL COMPONENT
// Floating panel for quick project switching
// ====================================================================

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useTimerStore } from '../stores/timerStore';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  PlayIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';

interface QuickStartProject {
  id: string;
  name: string;
  defaultActivityId: string | null;
  defaultActivityName: string | null;
  timeToday: number; // in minutes
}

interface QuickStartPanelProps {
  companyId: string;
}

const QuickStartPanel: React.FC<QuickStartPanelProps> = ({ companyId }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { isRunning, projectId: activeProjectId, quickStartProject } = useTimerStore();

  // Get current user's employee_id
  const { data: employeeId } = useQuery({
    queryKey: ['currentEmployeeId'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      return data?.id || null;
    },
  });

  // Fetch top projects from today
  const { data: topProjects = [], isLoading } = useQuery<QuickStartProject[]>({
    queryKey: ['quickStartProjects', companyId, employeeId],
    queryFn: async () => {
      if (!employeeId) return [];

      const today = new Date().toISOString().split('T')[0];

      // Get all time entries from today for this employee
      const { data: entries, error } = await supabase
        .from('time_entries')
        .select(`
          project_id,
          start_time,
          end_time,
          break_duration,
          projects (
            id,
            name
          ),
          activities (
            id,
            name
          )
        `)
        .eq('employee_id', employeeId)
        .eq('date', today)
        .order('created_at', { ascending: false });

      if (error || !entries) {
        console.error('Error fetching quick start projects:', error);
        return [];
      }

      // Group by project and calculate total time
      const projectMap = new Map<string, QuickStartProject>();

      entries.forEach((entry: any) => {
        const projectId = entry.project_id;
        if (!projectId || !entry.projects) return;

        // Calculate entry duration in minutes
        const parseTime = (timeStr: string) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        };

        const startMins = parseTime(entry.start_time);
        const endMins = parseTime(entry.end_time);
        let totalMins = endMins - startMins;
        if (totalMins < 0) totalMins += 24 * 60;
        const netMins = Math.max(0, totalMins - (entry.break_duration || 0));

        // Update or create project entry
        if (projectMap.has(projectId)) {
          const existing = projectMap.get(projectId)!;
          existing.timeToday += netMins;
        } else {
          projectMap.set(projectId, {
            id: projectId,
            name: entry.projects.name,
            defaultActivityId: entry.activities?.id || null,
            defaultActivityName: entry.activities?.name || null,
            timeToday: netMins,
          });
        }
      });

      // Get all active projects (in case some haven't been used today)
      const { data: allProjects } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');

      // Add projects that haven't been used today yet
      if (allProjects) {
        allProjects.forEach((project: any) => {
          if (!projectMap.has(project.id)) {
            projectMap.set(project.id, {
              id: project.id,
              name: project.name,
              defaultActivityId: null,
              defaultActivityName: null,
              timeToday: 0,
            });
          }
        });
      }

      // Convert to array and sort by time (most used first)
      const projectsArray = Array.from(projectMap.values());
      projectsArray.sort((a, b) => b.timeToday - a.timeToday);

      // Return top 4 projects
      return projectsArray.slice(0, 4);
    },
    enabled: !!employeeId && !!companyId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Get default activity for a project
  const { data: activities = [] } = useQuery({
    queryKey: ['activities', companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from('activities')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');
      return data || [];
    },
    enabled: !!companyId,
  });

  const handleQuickStart = async (project: QuickStartProject) => {
    // Use project's default activity, or first available activity
    const activityId = project.defaultActivityId || activities[0]?.id;
    
    if (!activityId) {
      return;
    }

    await quickStartProject(project.id, activityId, '');
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  if (isLoading || !employeeId) {
    return null;
  }

  return (
    <div className="fixed top-20 right-4 z-40 w-72 md:w-80">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div 
          className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div className="flex items-center space-x-2">
            <span className="text-xl">⚡</span>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Quick Start
            </h3>
          </div>
          <button
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            aria-label={isCollapsed ? 'Uitvouwen' : 'Inklappen'}
          >
            {isCollapsed ? (
              <ChevronDownIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronUpIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        {/* Content */}
        {!isCollapsed && (
          <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
            {topProjects.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                Geen projecten beschikbaar
              </div>
            ) : (
              topProjects.map((project) => {
                const isActive = isRunning && activeProjectId === project.id;
                
                return (
                  <div
                    key={project.id}
                    className={`
                      p-3 rounded-lg border transition-all
                      ${isActive 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700' 
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
                      }
                    `}
                  >
                    {/* Project Name */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className={`
                          font-medium text-sm truncate
                          ${isActive 
                            ? 'text-blue-900 dark:text-blue-100' 
                            : 'text-gray-900 dark:text-white'
                          }
                        `}>
                          {project.name}
                        </h4>
                        {project.defaultActivityName && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            {project.defaultActivityName}
                          </p>
                        )}
                      </div>
                      
                      {/* Time Today */}
                      {project.timeToday > 0 && (
                        <div className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400 ml-2">
                          <ClockIcon className="h-3 w-3" />
                          <span>{formatTime(project.timeToday)}</span>
                        </div>
                      )}
                    </div>

                    {/* Start Button */}
                    <button
                      onClick={() => handleQuickStart(project)}
                      disabled={isActive}
                      className={`
                        w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
                        ${isActive
                          ? 'bg-blue-600 text-white cursor-default'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }
                        disabled:opacity-50
                      `}
                    >
                      {isActive ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                          </span>
                          <span>Loopt</span>
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4" />
                          <span>Start</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })
            )}

            {/* Info Text */}
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-2 border-t border-gray-200 dark:border-gray-700">
              Klik op Start om snel te switchen
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickStartPanel;